<!--
  - Copyright (c) 2025.  Chad and contributors.
  - All rights reserved.
  -->

<template>
  <div>
    <v-card v-if="localExports.length > 0" class="overflow-hidden">
      <v-card-text>
        <div class="relative">
          <div v-if="loading" class="absolute inset-0 bg-white/50 flex items-center justify-center">
            <v-progress-circular color="primary" indeterminate></v-progress-circular>
          </div>

          <div class="overflow-x-auto">
            <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
              <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th class="px-4 sm:px-6 py-4" scope="col">File Name</th>
                <th class="px-4 sm:px-6 py-4" scope="col">Type</th>
                <th class="px-4 sm:px-6 py-4" scope="col">Size</th>
                <th class="px-4 sm:px-6 py-4" scope="col">Status</th>
                <th class="px-4 sm:px-6 py-4" scope="col">Created Date</th>
                <th class="px-4 sm:px-6 py-4 hidden sm:table-cell" scope="col">Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="dataExport in localExports"
                  :key="dataExport.id"
                  class="border-b hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <td class="px-4 sm:px-6 py-4 font-medium text-gray-900 dark:text-white">
                    <span class="cursor-pointer text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                          @click="openExport(dataExport.download_url)">
                      {{ dataExport.name }}
                    </span>
                </td>
                <td class="px-4 sm:px-6 py-4">
                    <span class="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-xs">
                      {{ dataExport.file_type }}
                    </span>
                </td>
                <td class="px-4 sm:px-6 py-4">
                  {{ humanReadableFileSize(dataExport.file_size) }}
                </td>
                <td class="px-4 sm:px-6 py-4">
                    <span :class="getStatusClasses(dataExport.status)">
                      {{ dataExport.status }}
                    </span>
                </td>
                <td class="px-4 sm:px-6 py-4">
                  {{ formatDate(dataExport.created_at) }}
                </td>
                <td class="px-4 sm:px-6 py-4">
                  <v-btn class="hover:bg-red-50 dark:hover:bg-red-900/20"
                         color="error"
                         size="small"
                         variant="text"
                         @click="confirmDelete(dataExport)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <div v-else class="text-center py-8">
      <v-icon class="mb-4" color="grey" size="48">mdi-file-download-outline</v-icon>
      <p class="text-gray-500 text-sm">Your order exports will appear here.</p>
    </div>

    <v-dialog v-model="deleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6">Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete this export? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" variant="text" @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="text" @click="deleteExport(selectedExport)">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts" setup>
import {onMounted, ref} from 'vue';
import {useExports} from '@/composables/useExports';
import {humanReadableFileSize} from '@/utils/file';
import {FileExport} from '@/store/modules/files';

const {getCompanyExports, deleteCompanyExport} = useExports();

const localExports = ref([]);
const loading = ref(true);
const deleteDialog = ref(false);
const selectedExport = ref(null);

const getStatusClasses = (status: string) => {
  const baseClasses = 'px-3 py-1 rounded-full text-xs font-medium';
  switch (status) {
    case 'pending':
      return `${baseClasses} text-yellow-800 bg-yellow-100 dark:text-yellow-300 dark:bg-yellow-900/30`;
    case 'completed':
      return `${baseClasses} text-green-800 bg-green-100 dark:text-green-300 dark:bg-green-900/30`;
    case 'failed':
      return `${baseClasses} text-red-800 bg-red-100 dark:text-red-300 dark:bg-red-900/30`;
    case 'processing':
      return `${baseClasses} text-blue-800 bg-blue-100 dark:text-blue-300 dark:bg-blue-900/30`;
    default:
      return `${baseClasses} text-gray-800 bg-gray-100 dark:text-gray-300 dark:bg-gray-900/30`;
  }
};

const formatDate = (date: string) => {
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const openExport = (s3Path: string) => {
  window.open(s3Path, '_blank');
};

const confirmDelete = (dataExport: FileExport) => {
  selectedExport.value = dataExport;
  deleteDialog.value = true;
};

const deleteExport = (dataExport: FileExport) => {
  loading.value = true;
  deleteCompanyExport(dataExport.id)
    .then(() => {
      localExports.value = localExports.value.filter((exportItem) => exportItem.id !== dataExport.id);
    })
    .catch((error) => {
      console.error('Error deleting export:', error);
    })
    .finally(() => {
      loading.value = false;
      deleteDialog.value = false;
    });
};

onMounted(() => {
  loading.value = true;
  getCompanyExports()
    .then((response) => {
      localExports.value = response.data;
    })
    .catch((error) => {
      console.error('Error fetching exports:', error);
    })
    .finally(() => {
      loading.value = false;
    });
});
</script>